import React, { useState } from 'react';
import { Copy, X } from 'lucide-react';
import { SignupCodeResponse } from 'services/api';

interface GenerateSignupCodeModalProps {
  isOpen: boolean;
  loading?: boolean;
  generatedCode?: SignupCodeResponse | null;
  onClose: () => void;
  onGenerate: (params: { expiresInDays: number; maxUses: number }) => Promise<void>;
}

const GenerateSignupCodeModal: React.FC<GenerateSignupCodeModalProps> = ({
  isOpen,
  loading = false,
  generatedCode,
  onClose,
  onGenerate,
}) => {
  const [expiresInDays, setExpiresInDays] = useState(30);
  const [maxUses, setMaxUses] = useState(10);
  const [formError, setFormError] = useState<string | null>(null);

  if (!isOpen) {
    return null;
  }

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!Number.isInteger(expiresInDays) || expiresInDays < 1 || expiresInDays > 365) {
      setFormError('Expiration must be between 1 and 365 days.');
      return;
    }

    if (!Number.isInteger(maxUses) || maxUses < 1 || maxUses > 1000) {
      setFormError('Max uses must be between 1 and 1000.');
      return;
    }

    await onGenerate({ expiresInDays, maxUses });
  };

  const handleCopy = async (value: string) => {
    await navigator.clipboard.writeText(value);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Generate Signup Code</h2>
            <p className="text-sm text-gray-600">Create a reusable code for employee onboarding</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <form onSubmit={handleGenerate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Expiration (days)</label>
              <input
                type="number"
                min={1}
                max={365}
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Maximum Uses</label>
              <input
                type="number"
                min={1}
                max={1000}
                value={maxUses}
                onChange={(e) => setMaxUses(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {formError ? <p className="text-sm text-red-600">{formError}</p> : null}

            <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60">
              {loading ? 'Generating...' : 'Generate Code'}
            </button>
          </form>

          {generatedCode ? (
            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <p className="text-sm font-medium text-green-800 mb-2">Code generated successfully</p>
              <div className="flex items-center justify-between gap-3 bg-white border border-green-200 rounded-lg p-3">
                <code className="text-lg font-semibold tracking-widest text-gray-900">{generatedCode.code}</code>
                <button
                  type="button"
                  onClick={() => handleCopy(generatedCode.code)}
                  className="inline-flex items-center gap-1 text-sm text-primary-700 hover:text-primary-900"
                >
                  <Copy className="h-4 w-4" />
                  Copy
                </button>
              </div>
              <p className="mt-2 text-xs text-green-700">
                This code can be used up to {generatedCode.maxUses} times before it expires.
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default GenerateSignupCodeModal;
